import { FC, useMemo } from "react";
import { shippingCarrierApi } from "../../../api/shippingCarrier.api";
import Input, { IInput, TInputEvent } from "../../form/Input";

/**
 * The carrier field: a dropdown of the configured shipping carriers.
 *
 * Two cases the CS portal's version also handles. A carrier already on the
 * item that is no longer in the list still has to be offered, or opening the
 * form on an old order would silently blank it. And where the value has been
 * changed, the original is shown underneath and can be clicked to come back -
 * the field is usually opened to correct a typo, not to replace a carrier.
 *
 * If the list cannot be loaded the field falls back to free text rather than
 * an empty dropdown, so tracking can still be saved.
 */
const ShipperSelect: FC<IInput> = ({ defaultValue, value, handle, ...props }) => {
  const { data: carriers, isError } = shippingCarrierApi.useGetShippingCarriersQuery();

  const names = useMemo(() => {
    const visible = (carriers || []).filter(({ visible }) => visible).map(({ name }) => name);
    // -- Whatever the item already carries belongs in the list too.
    if (value && !visible.includes(String(value))) visible.push(String(value));
    return visible.sort((a, b) => a.localeCompare(b));
  }, [carriers, value]);

  const resetValue = () => handle && handle({ target: { value: defaultValue } } as TInputEvent);

  return (
    <Input
      {...props}
      // -- No options at all means a plain text input, which is the right
      // -- fallback: better to type a carrier than to be unable to pick one.
      {...(isError || !names.length
        ? {}
        : { options: names.map((name) => ({ title: name, value: name })) })}
      value={value || ''}
      handle={handle}
      description={
        defaultValue && value !== defaultValue ? (
          <>
            Original carrier:{' '}
            <span
              className="text-primary hover:text-primary_hover underline"
              role="button"
              tabIndex={-1}
              onClick={resetValue}
            >
              {defaultValue}
            </span>
          </>
        ) : undefined
      }
    />
  );
};

export default ShipperSelect;
